'use client';
import IconFs2 from '@/assets/icons/category-second-level/icon-fs-2.svg';
import IconFs from '@/assets/icons/category-second-level/icon-fs.svg';
import CompareActive from '@/assets/icons/compare-active.svg';
import DefaultProductPlaceholder from '@/assets/images/Yellow.png';
import { CustomCardCarousel } from '@/components';
import {
  AddFavoriteMutation,
  AddVariantToComparisonMutation,
  AddViewedVariantMutation,
  AttributeBasicTranslationFragment,
  AttributeValueBasicTranslationFragment,
  DraftOrderCreateMutation,
  DraftOrderLineCreateMutation,
  ProductVariantAvailabilityEnum,
  RemoveFavoriteMutation,
  RemoveVariantFromComparisonMutation,
  StickerFragment,
  useAddFavoriteMutation,
  useAddVariantToComparisonMutation,
  useAddViewedVariantMutation,
  useDefaultChannelQuery,
  useDraftOrderCreateMutation,
  useDraftOrderLineCreateMutation,
  useDraftOrderQuery,
  useRemoveFavoriteMutation,
  useRemoveVariantFromComparisonMutation,
  useUserDraftOrdersQuery,
  useUserFavoritesQuery,
} from '@/graphql';
import { ProductLabel } from '@/graphql/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import { Link, useRouter } from '@/i18n/navigation';
import comparisonStore from '@/zustand/comparison';
import { favoritesStore } from '@/zustand/favourite';
import { useGuestStore } from '@/zustand/guestStore';
import { ordersStore } from '@/zustand/orders';
import useUserStore from '@/zustand/useUser';
import viewedVariantStore from '@/zustand/viewedVariant';
import { motion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { IoHeartOutline, IoHeartSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { Button } from 'rsuite';
import styles from './second-card.module.scss';

export type SelectedAttributeType = {
  __typename: 'SelectedAttribute';
  attribute: AttributeBasicTranslationFragment;
  values: AttributeValueBasicTranslationFragment[];
};

interface SingleProductImage {
  __typename: 'ProductMedia';
  id: string;
  alt: string;
  url: string;
}

export type ProductImageArray = SingleProductImage[];

const placeholderImageForCarousel: ProductImageArray = [
  {
    __typename: 'ProductMedia',
    id: 'default-placeholder',
    alt: 'Default product image',
    url: DefaultProductPlaceholder.src,
  },
];

type Props = {
  id?: string;
  slug?: string;
  categorySlug?: string;
  images:
    | { __typename: 'ProductMedia'; id: string; alt: string; url: string }[]
    | null
    | undefined;
  attributes: SelectedAttributeType[];
  preOrder: boolean;
  gift?: boolean;
  bonus?: number | undefined;
  title: string | undefined;
  pricePerMonth: number | undefined;
  price: number | undefined;
  labels?: StickerFragment[];
  priceUndiscounted?: number | undefined;
  discount?: number | undefined;
  reviews: number | null | undefined;
  sku: string | null | undefined;
  refetch?: () => void;
  productId?: string;
  availability?: ProductVariantAvailabilityEnum | null | undefined;
};

const CardSecond = ({
  id,
  slug,
  categorySlug,
  attributes,
  images,
  preOrder,
  bonus,
  title,
  pricePerMonth,
  price,
  labels,
  priceUndiscounted = undefined,
  discount = undefined,
  reviews,
  sku,
  refetch,
  productId,
  availability,
}: Props) => {
  const [openCartModal, setOpenCartModal] = useState(false);
  const router = useRouter();
  const userData = useUserStore((user: any) => user?.user);
  const setOrders = ordersStore(e => e.setOrders);
  const setFavorites = favoritesStore(e => e.setFavorites);
  const isLoggedIn = Boolean(userData?.id);
  const orderId = useGuestStore(state => state.guestOrderId);

  const setComparison = comparisonStore(e => e.setComparison);
  const comparison = comparisonStore(e => e.comparison);
  const guestFavoriteListArray = useGuestStore(state => state.favoriteList);
  const t = useTranslations('Components.card-second');
  const setViewedVariant = viewedVariantStore(e => e.setViewedVariant);
  const locale = useLocale();
  const { data: favoriteData, refetch: refetchFavoriteData } =
    useUserFavoritesQuery({
      variables: { first: 10 },
      context: {
        requiresAuth: true,
      },
    });

  const isFavorite = isLoggedIn
    ? favoriteData?.me?.favorites?.edges?.some(fav => fav?.node?.id === id)
    : guestFavoriteListArray?.includes(id);

  const isInComparison = comparison?.some(
    (comparison: any) => comparison.id === id
  );

  const handleSuccessOrder = (data: DraftOrderCreateMutation) => {
    if (data?.draftOrderCreate?.errors?.length === 0) {
      setOrders(data?.draftOrderCreate?.order);
      refetchDraftOrders();
    }
  };
  const handleSuccessOrderLine = (data: DraftOrderLineCreateMutation) => {
    if (data?.orderLinesCreate?.errors?.length === 0) {
      setOrders(data?.orderLinesCreate?.order);
      refetchDraftOrders();
    }
  };

  const { data: channelData } = useDefaultChannelQuery();

  const [addProductOrderLineToCart] = useDraftOrderLineCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: handleSuccessOrderLine,
  });

  const { data: draftOrdersData, refetch: refetchDraftOrders } =
    useUserDraftOrdersQuery({
      variables: {
        userId: userData?.id,
      },
    });

  const { data: guestDraftOrdersData } = useDraftOrderQuery({
    variables: {
      id: orderId || '',
    },
    skip: !orderId,
  });
  const [addProductToCart] = useDraftOrderCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: handleSuccessOrder,
  });

  const handleSuccessVariant = (data: AddViewedVariantMutation) => {
    if (data?.addViewedVariant?.errors?.length === 0) {
      setViewedVariant(data?.addViewedVariant?.user?.viewedVariants?.edges);
      refetch?.();
    }
  };

  const [createVariant] = useAddViewedVariantMutation({
    onCompleted: handleSuccessVariant,
    context: {
      requiresAuth: true,
    },
  });

  const addToVariant = async (id: any) => {
    if (!isLoggedIn) {
      useGuestStore.getState().addToViewedList(id);
      return;
    }
    try {
      const result = await createVariant({
        variables: {
          variant: id,
        },
      });
    } catch (error) {
      console.error('Error on adding variant: ', error);
    }
  };

  const handleSuccess = (data: AddFavoriteMutation) => {
    if (data?.addFavorite?.errors?.length === 0) {
      setFavorites(data?.addFavorite?.user?.favorites?.edges || []);
    }
  };

  const handleDelete = (data: RemoveFavoriteMutation) => {
    if (data?.removeFavorite?.errors?.length === 0) {
      setFavorites(data?.removeFavorite?.user?.favorites?.edges || []);
      refetch?.();
    }
  };

  const [create] = useAddFavoriteMutation({
    onCompleted: handleSuccess,
    context: {
      requiresAuth: true,
    },
  });

  const [remove] = useRemoveFavoriteMutation({
    onCompleted: handleDelete,
    context: {
      requiresAuth: true,
    },
  });

  const handleSuccessComparison = (data: AddVariantToComparisonMutation) => {
    if (data?.addVariantToComparison?.errors?.length === 0) {
      setComparison(data?.addVariantToComparison?.user?.comparison);
    }
  };

  const handleDeleteComparison = (
    data: RemoveVariantFromComparisonMutation
  ) => {
    if (data?.removeVariantFromComparison?.errors?.length === 0) {
      setComparison(data.removeVariantFromComparison?.user?.comparison);
    }
  };

  const [createComparison] = useAddVariantToComparisonMutation({
    onCompleted: handleSuccessComparison,
    context: {
      requiresAuth: true,
    },
  });

  const [removeComparison] = useRemoveVariantFromComparisonMutation({
    onCompleted: handleDeleteComparison,
    context: {
      requiresAuth: true,
    },
  });

  const addToFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        const result = await create({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().addToFavorite(id);
      }
    } catch (error) {
      console.error('Error on adding to favorite: ', error);
    }
  };
  const removeFromFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        await remove({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().removeFromFavorite(id);
      }
    } catch (error) {
      console.error('Error on removing from favorite: ', error);
    }
  };

  const addToComparison = async (id: any) => {
    try {
      if (!isLoggedIn) {
        useGuestStore.getState().addToComparison(id);
      } else {
        await createComparison({
          variables: {
            variant: id,
          },
        });
      }
    } catch (error) {
      console.error('Error on adding to comparison: ', error);
    }
  };

  const removeFromComparison = async (id: any) => {
    try {
      if (isLoggedIn) {
        await removeComparison({
          variables: {
            variant: id,
          },
        });
      } else {
        useGuestStore.getState().removeFromComparison(id);
      }
    } catch (error) {
      console.error('Error on removing from Comparison: ', error);
    }
  };

  const handleCartOpen = () => {
    setOpenCartModal(true);
  };

  const handleAddToBasket = async (id: any) => {
    if (availability === 'OUT_OF_STOCK') {
      toast.error(`Товара нет в наличии.`);
      return;
    }
    if (availability === 'COMING_SOON') {
      toast.info(`Товар скоро появится.`);
      return;
    }
    try {
      if (isLoggedIn) {
        if (draftOrdersData?.userDraftOrders?.length === 0) {
          await addProductToCart({
            variables: {
              input: {
                channelId: channelData?.channel?.id,
                user: userData?.id,
                lines: [
                  {
                    variantId: id,
                    quantity: 1,
                  },
                ],
              },
            },
          });
        }
        if (draftOrdersData?.userDraftOrders?.[0]?.isFastBuy === false) {
          await addProductOrderLineToCart({
            variables: {
              id: draftOrdersData?.userDraftOrders?.[0]?.id,
              input: [
                {
                  variantId: id,
                  quantity: 1,
                },
              ],
            },
          });
        }
      } else {
        if (!orderId) {
          const orderGuestData = await addProductToCart({
            variables: {
              input: {
                channelId: channelData?.channel?.id,
                lines: [
                  {
                    variantId: id,
                    quantity: 1,
                  },
                ],
              },
            },
          });
          const guestOrderId =
            orderGuestData?.data?.draftOrderCreate?.order?.id;
          typeof guestOrderId === 'string' &&
            useGuestStore.getState().setGuestOrderId(guestOrderId);
        } else {
          await addProductOrderLineToCart({
            variables: {
              id: orderId,
              input: [
                {
                  variantId: id,
                  quantity: 1,
                },
              ],
            },
          });
        }
      }
      handleCartOpen();
    } catch (error) {
      console.error('Error on adding to Basket', error);
    }
  };

  const isProductInBasket: number | undefined = isLoggedIn
    ? draftOrdersData?.userDraftOrders?.[0]?.lines.filter(
        product => product?.variant?.id == id
      ).length
    : guestDraftOrdersData?.draftOrder?.lines.filter(
        product => product?.variant?.id == id
      ).length;

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.productInfo}>
        <div className={styles.imageSlider}>
          <CustomCardCarousel
            images={
              images && images?.length > 0
                ? images
                : placeholderImageForCarousel
            }
            id={id}
            slug={slug}
            categorySlug={categorySlug}
            productId={productId ?? '#'}
          />
        </div>
        <div className={styles.texts}>
          <div className={styles.topSection}>
            <Link
              href={
                id && productId
                  ? `/product/${decodeBase64Id(id)}-${slug}`
                  : `/catalog/category/smartphones/xiaomi`
              }
              onClick={() => addToVariant(id)}
              className={styles.title}
            >
              {title}
            </Link>
            <div className={styles.subtitle}>
              <span className={styles.comment}>
                {reviews} {t('reviews')}
              </span>
              <span className={styles.codeProduct}>
                {t('code-product')}: {sku}
              </span>
            </div>
            <div className={styles.discounts}>
              {bonus !== undefined && bonus !== null && bonus !== 0 && (
                <div className={styles.bonus}>{t('bonus')}</div>
              )}
              {discount !== undefined &&
                discount !== null &&
                discount !== 0 && (
                  <div className={styles.disc}>{t('discount')}</div>
                )}
              {/* <div className={styles.percent}>-15%</div> */}
              {labels?.map(label => (
                <div
                  className={styles.labels}
                  style={{
                    backgroundColor: `${label.backgroundColor}`,
                    color: `${label.color}`,
                  }}
                  key={label.id}
                >
                  {label.content}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.characteristics}>
            {attributes
              ?.filter(i => i.attribute.isMain)
              .slice(0, 4)
              .map(att => {
                return (
                  <div className={styles.item} key={att?.attribute?.id}>
                    <div className={styles.name}>
                      {locale === 'ru'
                        ? att?.attribute?.name
                        : att?.attribute?.translation?.name}
                    </div>
                    <div className={styles.info}>
                      {locale === 'ru'
                        ? att?.values[0]?.name
                        : att?.values[0]?.translation?.name}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      <div className={styles.productPrice}>
        <div className={styles.topSection}>
          <div className={styles.icons}>
            <motion.div
              whileTap={{ scale: 2 }}
              transition={{ duration: 0.5 }}
              onClick={() =>
                isFavorite ? removeFromFavorite(id) : addToFavorite(id)
              }
              style={{
                cursor: 'pointer',
              }}
            >
              {isFavorite ? (
                <Button type="button" className={styles.btn}>
                  <div className={styles.like}>
                    <IoHeartSharp size={25} color="red" />
                  </div>
                </Button>
              ) : (
                <Button type="button" className={styles.btn}>
                  <div className={styles.like}>
                    <IoHeartOutline size={25} />
                  </div>
                </Button>
              )}
            </motion.div>
            <Button
              onClick={() =>
                isInComparison ? removeFromComparison(id) : addToComparison(id)
              }
              type="button"
              className={styles.btn}
            >
              <div className={isInComparison ? styles.fs1Active : styles.fs1}>
                {isInComparison ? <CompareActive /> : <IconFs />}
              </div>
              <div className={isInComparison ? styles.fs2Active : styles.fs2}>
                {isInComparison ? <CompareActive /> : <IconFs2 />}
              </div>
            </Button>
          </div>

          {pricePerMonth !== undefined &&
            pricePerMonth !== null &&
            pricePerMonth !== 0 && (
              <p className={styles.monthPrice}>
                {t('from')} {convertToReadable(Math.round(pricePerMonth / 12))}{' '}
                {t('price-month')}
              </p>
            )}
        </div>
        <div className={styles.prices}>
          {discount ? (
            <>
              <p className={styles.discountPrice}>
                {convertToReadable(priceUndiscounted)} {t('price')}
              </p>
              <p className={styles.price}>
                {convertToReadable(price)} {t('price')}
              </p>
            </>
          ) : (
            <p className={styles.price}>
              {convertToReadable(priceUndiscounted)} {t('price')}
            </p>
          )}

          {bonus !== undefined && bonus !== null && bonus !== 0 && (
            <span className={styles.cashback}>
              {convertToReadable(bonus)} {t('cashback')}
            </span>
          )}
        </div>
        <div className={styles.btnGroup}>
          {preOrder ? (
            <Button type="button" className={styles.btn}>
              {t('pre-order')}
            </Button>
          ) : (
            <>
              {' '}
              <Button
                onClick={() => handleAddToBasket(id)}
                type="button"
                className={styles.btnBuy}
              >
                {!!isProductInBasket ? t('product-in-cart') : t('to-cart')}
              </Button>
              <Button
                onClick={() => router.push('/order')}
                type="button"
                className={styles.buyNow}
              >
                {t('order-now')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardSecond;
